// FacilitiesViewPanel - Browse all community-created facilities
// Shows facilities with filtering, search, and navigation

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  OilBarrel as OilBarrelIcon,
  Map as MapIcon,
  Share as ShareIcon,
  StarBorder as StarBorderIcon,
  ContentCopy as ContentCopyIcon,
  LocationOn as LocationOnIcon,
  Google as GoogleIcon,
  PhotoLibrary as PhotoLibraryIcon,
  AddAPhoto as AddAPhotoIcon,
} from '@mui/icons-material';
import { CyberSpinner } from './CyberSpinner';
import { telegram } from '../services/telegram';
import { getCyberColors } from '../theme/cyberColors';
import { useAppStore } from '../stores/appStore';

interface CommunityFacility {
  id: string;
  name: string;
  type: string;
  api_number?: string;
  operator?: string;
  latitude: number;
  longitude: number;
  county?: string;
  state?: string;
  photo_url?: string;
  description?: string;
  created_at: string;
  created_by?: string;
}

interface FacilitiesViewPanelProps {
  open: boolean;
  onClose: () => void;
  onNavigate?: (lat: number, lng: number, name: string) => void;
  onAddPhoto?: (facility: { id: string; name: string; type: string; latitude: number; longitude: number }) => void;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

const FACILITY_TYPE_INFO: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  well: { icon: <OilBarrelIcon />, label: 'Well', color: '#4ade80' },
  swd: { icon: <OilBarrelIcon />, label: 'SWD', color: '#60a5fa' },
  rig: { icon: <BusinessIcon />, label: 'Rig', color: '#f59e0b' },
  compressor: { icon: <BusinessIcon />, label: 'Compressor', color: '#a78bfa' },
  tank_battery: { icon: <BusinessIcon />, label: 'Tank Battery', color: '#fb923c' },
  other: { icon: <BusinessIcon />, label: 'Other', color: '#94a3b8' },
};

export function FacilitiesViewPanel({ open, onClose, onNavigate, onAddPhoto }: FacilitiesViewPanelProps) {
  const darkMode = useAppStore((state) => state.darkMode);
  const highContrast = useAppStore((state) => state.highContrast);
  const colors = getCyberColors(darkMode, highContrast);
  const cardGlow = colors.neonBlue || colors.neonCyan || '#60a5fa';
  const defaultState = 'NM';

  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState<CommunityFacility[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      telegram.hapticFeedback('success');
    } catch (err) {
      console.error('Copy failed', err);
      telegram.showAlert?.('Unable to copy text');
    }
  }, []);

  const shareFacility = (facility: CommunityFacility) => {
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${facility.latitude},${facility.longitude}`;
    const shareText = `${facility.name}\n${facility.api_number ? `API: ${facility.api_number}\n` : ''}${facility.county ? `${facility.county}${facility.state ? `, ${facility.state}` : ''}\n` : ''}${mapsUrl}`;
    telegram.webApp?.openTelegramLink?.(
      `https://t.me/share/url?url=${encodeURIComponent(mapsUrl)}&text=${encodeURIComponent(shareText)}`
    );
    telegram.hapticFeedback('light');
  };

  const openGoogleMaps = (facility: CommunityFacility) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${facility.latitude},${facility.longitude}&travelmode=driving`;
    window.open(url, '_blank');
    telegram.hapticFeedback('light');
  };

  // Fetch facilities from API
  const fetchFacilities = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/community-facilities?limit=100`);
      if (response.ok) {
        const data = await response.json();
        const mappedFacilities = (data.facilities || []).map((f: any) => ({
          ...f,
          // Normalize keys if backend returns facility_type/api_number
          type: f.type || f.facility_type || 'other',
          api_number: f.api_number,
          county: f.county,
          operator: f.operator,
          photo_url: f.photo_url,
          state: f.state || defaultState,
        }));
        setFacilities(mappedFacilities);
      }
    } catch (error) {
      console.error('Failed to fetch facilities:', error);
      telegram.showAlert('Failed to load facilities');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on open
  useEffect(() => {
    if (open) {
      fetchFacilities();
    }
  }, [open, fetchFacilities]);

  // Filter facilities
  const filteredFacilities = facilities.filter((facility) => {
    // Type filter
    if (filterType !== 'all' && facility.type !== filterType) {
      return false;
    }

    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        facility.name.toLowerCase().includes(query) ||
        facility.type.toLowerCase().includes(query) ||
        facility.description?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  const getFacilityInfo = (type: string) => {
    return FACILITY_TYPE_INFO[type] || FACILITY_TYPE_INFO['other'];
  };

  const renderActionButton = (
    tooltip: string,
    icon: React.ReactNode,
    onClick: (e: React.MouseEvent) => void,
    color: string,
    hoverColor?: string,
    disabled?: boolean
  ) => (
    <Tooltip title={tooltip} arrow>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onClick(e);
          }}
          sx={{
            width: 36,
            height: 36,
            color,
            backgroundColor: `${color}15`,
            opacity: disabled ? 0.4 : 1,
            transition: 'transform 0.18s ease, box-shadow 0.18s ease',
            '&:hover': {
              backgroundColor: `${hoverColor || color}25`,
              transform: disabled ? 'none' : 'translateY(-1px)',
              boxShadow: disabled ? 'none' : `0 6px 12px ${color}30`,
            },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: darkMode ? 'rgba(15, 23, 42, 0.98)' : 'rgba(255, 255, 255, 0.98)',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        backdropFilter: 'blur(10px)',
      }}
    >
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 2,
          borderRadius: 0,
          bgcolor: darkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(248, 250, 252, 0.95)',
          borderBottom: `2px solid ${colors.neonCyan}50`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon sx={{ color: colors.neonCyan, fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? '#fff' : '#000' }}>
              Community Facilities
            </Typography>
            <Chip
              label={filteredFacilities.length}
              size="small"
              sx={{
                bgcolor: colors.neonCyan,
                color: '#fff',
                fontWeight: 700,
              }}
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Search and Filter */}
        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search facilities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.neonCyan }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: darkMode ? 'rgba(15, 23, 42, 0.5)' : 'rgba(255, 255, 255, 0.9)',
                '& fieldset': { borderColor: `${colors.neonCyan}30` },
                '&:hover fieldset': { borderColor: `${colors.neonCyan}60` },
                '&.Mui-focused fieldset': { borderColor: colors.neonCyan },
              },
            }}
          />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="All"
              onClick={() => setFilterType('all')}
              color={filterType === 'all' ? 'primary' : 'default'}
              size="small"
            />
            {Object.entries(FACILITY_TYPE_INFO).map(([type, info]) => (
              <Chip
                key={type}
                icon={info.icon as any}
                label={info.label}
                onClick={() => setFilterType(type)}
                color={filterType === type ? 'primary' : 'default'}
                size="small"
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CyberSpinner size={60} />
          </Box>
        ) : filteredFacilities.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {searchQuery || filterType !== 'all'
              ? 'No facilities match your search'
              : 'No facilities added yet'}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredFacilities.map((facility) => {
              const facilityInfo = getFacilityInfo(facility.type);
              // Get glow color based on facility type
              const facilityColor = facilityInfo.color || '#22c55e';
              const displayId = facility.api_number || facility.id;
              const iconNode = React.cloneElement(facilityInfo.icon as any, { sx: { color: facilityColor, fontSize: 18 } });
              return (
                <Box
                  key={facility.id}
                  sx={{
                    backgroundColor: darkMode ? '#0b1220' : '#ffffff',
                    border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                    borderRadius: '14px',
                    boxShadow: `0 10px 22px rgba(0,0,0,0.08), 0 0 14px ${cardGlow}25`,
                    px: 1.75,
                    py: 1.25,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 14px 30px rgba(0,0,0,0.12), 0 0 20px ${cardGlow}40`,
                      borderColor: `${facilityColor}50`,
                    }
                  }}
                >
                  {/* ROW 1: Icon + Facility Name */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: `${facilityColor}18`,
                        border: `2px solid ${facilityColor}60`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {iconNode}
                    </Box>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 700, 
                        color: darkMode ? '#e2e8f0' : '#0f172a',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '1.08rem',
                        lineHeight: 1.3,
                        flex: 1
                      }}
                    >
                      {facility.name}
                    </Typography>
                  </Box>

                  {/* ROW 2: Info Chips */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 0.75, 
                    flexWrap: 'wrap', 
                    alignItems: 'center',
                    overflow: 'hidden',
                    pb: 0.5,
                  }}>
                    {/* ID Chip - Blue */}
                    <Chip
                      icon={<ContentCopyIcon sx={{ fontSize: '0.85rem !important' }} />}
                      label={displayId}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(displayId);
                      }}
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                        backgroundColor: darkMode ? '#111827' : '#e0e7ff',
                        color: '#1d4ed8',
                        cursor: 'pointer',
                        '& .MuiChip-icon': { color: '#1d4ed8' },
                        '&:hover': { backgroundColor: darkMode ? '#0b1220' : '#d0deff' }
                      }}
                    />
                    {/* Coordinates Chip - Yellow */}
                    <Chip
                      label={`${facility.latitude.toFixed(4)}, ${facility.longitude.toFixed(4)}`}
                      size="small"
                      icon={<LocationOnIcon sx={{ fontSize: '0.85rem !important', color: '#ea580c' }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(`${facility.latitude}, ${facility.longitude}`);
                      }}
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                        backgroundColor: darkMode ? '#1f172a' : '#fde8d6',
                        color: '#b45309',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: darkMode ? '#2a1f34' : '#f7d8bd' }
                      }}
                    />
                    {/* State Chip */}
                    <Chip
                      label={facility.state || defaultState}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                        backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.12)' : 'rgba(37, 99, 235, 0.1)',
                        color: darkMode ? '#93c5fd' : '#2563eb',
                      }}
                    />
                    {/* County Chip */}
                    {facility.county && (
                      <Chip
                        label={facility.county}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                          backgroundColor: darkMode ? 'rgba(52, 211, 153, 0.1)' : 'rgba(6, 95, 70, 0.1)',
                          color: darkMode ? '#34d399' : '#065f46',
                        }}
                      />
                    )}
                    {/* Operator Chip */}
                    {facility.operator && (
                      <Chip
                        label={facility.operator}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                          backgroundColor: darkMode ? '#111827' : '#e5e7eb',
                          color: darkMode ? '#e5e7eb' : '#374151',
                          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }
                        }}
                      />
                    )}
                    {/* Type Chip - Green */}
                    <Chip
                      label={facilityInfo.label}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                        backgroundColor: darkMode ? 'rgba(52, 211, 153, 0.1)' : 'rgba(6, 95, 70, 0.1)',
                        color: darkMode ? '#34d399' : '#065f46',
                      }}
                    />
                    {/* Description Chip - Gray */}
                    {facility.description && (
                      <Chip
                        label={facility.description.slice(0, 25) + (facility.description.length > 25 ? '...' : '')}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                          backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                          color: darkMode ? '#ffffff' : '#334155',
                          maxWidth: 160,
                          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                        }}
                      />
                    )}
                  </Box>

                  {/* ROW 3: Actions */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.5 }}>
                    {renderActionButton(
                      'Favorite',
                      <StarBorderIcon sx={{ fontSize: '1rem' }} />,
                      () => telegram.hapticFeedback('light'),
                      '#475569'
                    )}
                    {renderActionButton(
                      'Show on map',
                      <MapIcon sx={{ fontSize: '1rem' }} />,
                      () => {
                        if (onNavigate) {
                          onNavigate(facility.latitude, facility.longitude, facility.name);
                          onClose();
                        }
                        telegram.hapticFeedback('medium');
                      },
                      '#2563eb'
                    )}
                    {renderActionButton(
                      'Share',
                      <ShareIcon sx={{ fontSize: '1rem' }} />,
                      () => shareFacility(facility),
                      '#16a34a'
                    )}
                    {renderActionButton(
                      'Google Maps',
                      <GoogleIcon sx={{ fontSize: '1rem' }} />,
                      () => openGoogleMaps(facility),
                      '#2563eb'
                    )}
                    {renderActionButton(
                      facility.photo_url ? 'View Photos' : 'Be first to add a photo!',
                      facility.photo_url 
                        ? <PhotoLibraryIcon sx={{ fontSize: '1rem' }} />
                        : <AddAPhotoIcon sx={{ fontSize: '1rem' }} />,
                      () => {
                        if (facility.photo_url) {
                          window.open(facility.photo_url, '_blank');
                        } else if (onAddPhoto) {
                          onAddPhoto({
                            id: facility.id,
                            name: facility.name,
                            type: facility.type,
                            latitude: facility.latitude,
                            longitude: facility.longitude
                          });
                        }
                      },
                      facility.photo_url ? '#a21caf' : '#6b7280',
                      facility.photo_url ? '#86198f' : '#4b5563',
                      !facility.photo_url && !onAddPhoto
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
